import { useEffect, useState } from "react";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { TimeSeriesChart, BreakdownBar } from "../components/AnalyticsCharts";
import { Copy, RefreshCw, Trash2, Plus, Check, Eye, EyeOff, ShieldCheck, Key, Package, Download, Send, BookOpen, Mail, Bell, BellOff, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_URL } from "../lib/api";

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [globalAnalytics, setGlobalAnalytics] = useState(null);
    const [visibleKeys, setVisibleKeys] = useState({});
    const [copiedKey, setCopiedKey] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newSlug, setNewSlug] = useState("");
    const [newName, setNewName] = useState("");
    const [sendMsgTenant, setSendMsgTenant] = useState(null);   // {tenant_id, slug, name, contact_email}
    const [sendMsgTo, setSendMsgTo] = useState("");
    const [sending, setSending] = useState(false);
    const [tenantSearch, setTenantSearch] = useState("");
    const [tenantsHealth, setTenantsHealth] = useState({});   // tenant_id → { last_link_at, links_24h, health }
    const [bulkOpen, setBulkOpen] = useState(false);          // bulk-send confirm dialog
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) {
            navigate("/dashboard");
        }
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const [{ data: t }, { data: g }, healthResp] = await Promise.all([
                api.get("/admin/tenants"),
                api.get("/admin/analytics/global"),
                api.get("/admin/tenants-health").catch(() => ({ data: { items: [] } })),
            ]);
            setTenants(t.items || []);
            setGlobalAnalytics(g);
            const hm = {};
            for (const item of (healthResp.data?.items || [])) {
                hm[item.tenant_id] = item;
            }
            setTenantsHealth(hm);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { if (user?.role === "admin") load(); }, [user]);

    const sendTestDpaEmail = async () => {
        try {
            const { data } = await api.post("/admin/dpa/test-email");
            if (data.status === "sent") {
                toast.success(`Test DPA email sent → ${data.to} (id: ${data.email_id})`);
            } else if (data.status === "skipped") {
                toast.error(`Skipped: ${data.reason}. Would have sent to ${data.would_send_to} from ${data.from}.`);
            } else {
                toast.error(`Send failed. Check backend logs for Resend error.`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test email failed");
        }
    };

    const rotate = async (id) => {
        if (!window.confirm("Rotate this API key? Existing integrations will stop working until updated.")) return;
        const { data } = await api.post(`/admin/tenants/${id}/rotate-key`);
        toast.success("API key rotated");
        setVisibleKeys((v) => ({ ...v, [id]: true }));
        setTenants((ts) => ts.map((t) => t.tenant_id === id ? { ...t, api_key: data.api_key } : t));
    };

    const toggleSuppress = async (t) => {
        const nextEnabled = !t.suppress_dead_link_alerts;
        const verb = nextEnabled ? "Suppress" : "Resume";
        if (!window.confirm(`${verb} dead-link alerts for "${t.slug}"?\n\n${nextEnabled ? "Nightly scanner will skip this tenant's links and any previously-flagged dead links from this tenant will be hidden from the Dead Links page. Useful during DNS-launch windows." : "Scanner will resume flagging this tenant's dead links."}`)) return;
        try {
            await api.patch(`/admin/tenants/${t.tenant_id}/dead-link-suppression`, { enabled: nextEnabled });
            toast.success(nextEnabled ? `Dead-link alerts suppressed for ${t.slug}` : `Dead-link alerts resumed for ${t.slug}`);
            setTenants((ts) => ts.map((x) => x.tenant_id === t.tenant_id ? { ...x, suppress_dead_link_alerts: nextEnabled } : x));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update");
        }
    };

    const copyFullEnv = async (t) => {
        try {
            const { data } = await api.get(`/admin/tenants/${t.tenant_id}/full-env-envelope.env`,
                { responseType: "text", transformResponse: (r) => r });
            const text = typeof data === "string" ? data : String(data);
            await navigator.clipboard.writeText(text);
            const hasSp = !text.includes("<PASTE FROM SP>");
            toast.success(hasSp
                ? `Full env copied for ${t.slug} (SpLink + SocialPulse)`
                : `SpLink env copied for ${t.slug} — SocialPulse creds still pending (placeholders included)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to copy env");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this tenant permanently?")) return;        try {
            await api.delete(`/admin/tenants/${id}`);
            toast.success("Tenant deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        }
    };

    const create = async () => {
        try {
            const { data: newTenant } = await api.post("/admin/tenants", {
                slug: newSlug.trim(), name: newName.trim(),
            });
            setCreating(false); setNewSlug(""); setNewName("");
            await load();
            // Success toast with a one-click "Send handout now" action so the
            // admin doesn't have to hunt for the row + open the send dialog.
            toast.success(`${newTenant.name} created`, {
                description: "Ready to email the integration handout?",
                duration: 10000,
                action: {
                    label: "Send handout now",
                    onClick: () => {
                        setSendMsgTenant({
                            tenant_id: newTenant.tenant_id,
                            slug: newTenant.slug,
                            name: newTenant.name,
                            contact_email: newTenant.contact_email || "",
                        });
                        setSendMsgTo(newTenant.contact_email || "");
                    },
                },
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create");
        }
    };

    const copyKey = async (key, id) => {
        await navigator.clipboard.writeText(key);
        setCopiedKey(id);
        setTimeout(() => setCopiedKey(null), 1400);
    };

    if (loading || !user || user.role !== "admin") {
        return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-7xl px-6 py-10">
                <div className="mb-8 flex items-end justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin console</div>
                        <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter" data-testid="admin-heading">
                            Ecosystem overview
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        <Button asChild variant="outline" data-testid="dead-links-btn">
                            <Link to="/admin/dead-links">
                                <RefreshCw className="mr-2 h-4 w-4" /> Dead links
                            </Link>
                        </Button>
                        <Button variant="outline" onClick={sendTestDpaEmail} data-testid="dpa-test-email-btn">
                            <Send className="mr-2 h-4 w-4" /> Test DPA email
                        </Button>
                        <Button asChild variant="outline" data-testid="consent-csv-btn">
                            <a href={`${API_URL}/admin/consent-log.csv`} download>
                                <Download className="mr-2 h-4 w-4" /> Consent CSV
                            </a>
                        </Button>
                        <Button asChild variant="outline" data-testid="api-keys-btn">
                            <Link to="/admin/api-keys">
                                <Key className="mr-2 h-4 w-4" /> API keys
                            </Link>
                        </Button>
                        <Button asChild variant="outline" data-testid="reserved-slugs-btn">
                            <Link to="/admin/reserved">
                                <ShieldCheck className="mr-2 h-4 w-4" /> Reserved slugs
                            </Link>
                        </Button>
                        <Dialog open={creating} onOpenChange={setCreating}>
                            <DialogTrigger asChild>
                                <Button data-testid="new-tenant-btn"><Plus className="mr-2 h-4 w-4" /> New tenant</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Create tenant</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label htmlFor="tenant-slug">Slug</Label>
                                        <Input id="tenant-slug" value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
                                            placeholder="mynewsite" className="mt-2 font-mono" data-testid="tenant-slug-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="tenant-name">Display name</Label>
                                        <Input id="tenant-name" value={newName} onChange={(e) => setNewName(e.target.value)}
                                            placeholder="My New Site" className="mt-2" data-testid="tenant-name-input" />
                                    </div>
                                    <Button onClick={create} className="w-full" data-testid="create-tenant-submit">Create tenant</Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Global metrics */}
                {globalAnalytics && (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <Metric label="Clicks" value={globalAnalytics.total_clicks} testId="admin-metric-clicks" />
                        <Metric label="Links" value={globalAnalytics.total_links} testId="admin-metric-links" />
                        <Metric label="Users" value={globalAnalytics.total_users} testId="admin-metric-users" />
                        <Metric label="Tenants" value={globalAnalytics.total_tenants} testId="admin-metric-tenants" />
                    </div>
                )}

                {globalAnalytics && (
                    <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
                        <div>
                            <h2 className="mb-3 font-display text-xl font-bold">Global clicks</h2>
                            <div className="border border-border bg-card p-4">
                                <TimeSeriesChart data={globalAnalytics.timeseries} />
                            </div>
                        </div>
                        <BreakdownBar data={globalAnalytics.by_tenant} title="Clicks by tenant" testId="chart-by-tenant" />
                    </div>
                )}

                {/* Tenants table */}
                <div className="mt-14">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="font-display text-xl font-bold">Tenants & API keys</h2>
                        <div className="flex items-center gap-2">
                            <Input
                                data-testid="tenant-search-input"
                                type="search"
                                placeholder={`Search ${tenants.length} tenants by slug or name…`}
                                value={tenantSearch}
                                onChange={(e) => setTenantSearch(e.target.value)}
                                className="w-64"
                            />
                            {tenantSearch && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="tenant-search-clear"
                                    onClick={() => setTenantSearch("")}
                                >
                                    Clear
                                </Button>
                            )}
                            {(() => {
                                const q = tenantSearch.trim().toLowerCase();
                                const filtered = (tenants || []).filter((t) => !q ||
                                    (t.slug || "").toLowerCase().includes(q) ||
                                    (t.name || "").toLowerCase().includes(q));
                                const withEmail = filtered.filter((t) => t.contact_email);
                                if (!filtered.length) return null;
                                return (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid="bulk-send-open"
                                        disabled={withEmail.length === 0}
                                        onClick={() => { setBulkResults(null); setBulkOpen(true); }}
                                        title={withEmail.length === 0
                                            ? "None of the filtered tenants have a contact_email on file — click the Send icon on a row to set one first."
                                            : `Email the site-message to all ${withEmail.length} filtered tenants that have a contact_email on file.`}
                                    >
                                        <Send className="mr-2 h-4 w-4" />
                                        Send to {withEmail.length}{withEmail.length !== filtered.length ? `/${filtered.length}` : ""}
                                    </Button>
                                );
                            })()}
                        </div>
                    </div>
                    <div className="overflow-x-auto border border-border bg-card">
                        <Table data-testid="tenants-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Slug</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>API key</TableHead>
                                    <TableHead className="text-right">Links</TableHead>
                                    <TableHead className="text-right">Clicks</TableHead>
                                    <TableHead className="text-right" title="Last time a link was minted (health tier: green <24h, amber <7d, red older, grey never)">Last shorten</TableHead>
                                    <TableHead className="text-right" title="Links minted in the last 24 hours">24h</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(() => {
                                    const q = tenantSearch.trim().toLowerCase();
                                    const filtered = (tenants || [])
                                        .filter((t) => !q || (t.slug || "").toLowerCase().includes(q) || (t.name || "").toLowerCase().includes(q))
                                        .slice()
                                        .sort((a, b) => (a.slug || "").localeCompare(b.slug || ""));
                                    if (filtered.length === 0) {
                                        return (
                                            <TableRow data-testid="tenant-search-empty">
                                                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                                                    No tenants match “{tenantSearch}”.
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return filtered.map((t) => (
                                    <TableRow key={t.tenant_id} data-testid={`tenant-row-${t.slug}`}>
                                        <TableCell className="font-mono text-primary">{t.slug}</TableCell>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <code className="font-mono text-xs">
                                                    {visibleKeys[t.tenant_id] ? t.api_key : "•".repeat(Math.min(32, (t.api_key || "").length))}
                                                </code>
                                                <Button size="icon" variant="ghost"
                                                    onClick={() => setVisibleKeys((v) => ({ ...v, [t.tenant_id]: !v[t.tenant_id] }))}
                                                    aria-label="Toggle visibility"
                                                    data-testid={`toggle-key-${t.slug}`}>
                                                    {visibleKeys[t.tenant_id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                                </Button>
                                                <Button size="icon" variant="ghost" onClick={() => copyKey(t.api_key, t.tenant_id)}
                                                    aria-label="Copy key" data-testid={`copy-key-${t.slug}`}>
                                                    {copiedKey === t.tenant_id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono">{t.links_count}</TableCell>
                                        <TableCell className="text-right font-mono">{t.clicks_count}</TableCell>
                                        <TableCell className="text-right text-xs" data-testid={`health-${t.slug}`}>
                                            {(() => {
                                                const h = tenantsHealth[t.tenant_id];
                                                const tier = h?.health || "grey";
                                                const dotColor = {
                                                    green: "bg-emerald-500",
                                                    amber: "bg-amber-500",
                                                    red: "bg-red-500",
                                                    grey: "bg-muted-foreground/30",
                                                }[tier];
                                                let label = "never";
                                                if (h?.last_link_at) {
                                                    try {
                                                        const ageMs = Date.now() - new Date(h.last_link_at).getTime();
                                                        const h_ = Math.floor(ageMs / 3_600_000);
                                                        const d_ = Math.floor(h_ / 24);
                                                        label = d_ >= 1 ? `${d_}d ago` : h_ >= 1 ? `${h_}h ago` : "<1h";
                                                    } catch { /* ignore */ }
                                                }
                                                return (
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} aria-hidden />
                                                        <span className="text-muted-foreground">{label}</span>
                                                    </div>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell className="text-right font-mono" data-testid={`links-24h-${t.slug}`}>
                                            {tenantsHealth[t.tenant_id]?.links_24h ?? 0}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button size="icon" variant="ghost" asChild aria-label="Download integration handbook" data-testid={`handbook-${t.slug}`}>
                                                    <a
                                                        href={`${API_URL}/admin/tenants/${t.tenant_id}/integration-guide.md`}
                                                        download
                                                        title="Download integration handbook (Markdown)"
                                                    >
                                                        <BookOpen className="h-4 w-4" />
                                                    </a>
                                                </Button>
                                                <Button size="icon" variant="ghost" asChild aria-label="Download site-message" data-testid={`site-message-${t.slug}`}>
                                                    <a
                                                        href={`${API_URL}/admin/tenants/${t.tenant_id}/site-message.md`}
                                                        download
                                                        title="Download site-message (short paste-ready update for this sister-site's agent)"
                                                    >
                                                        <Mail className="h-4 w-4" />
                                                    </a>
                                                </Button>
                                                <Button
                                                    size="icon" variant="ghost"
                                                    aria-label="Send site-message via email"
                                                    data-testid={`send-message-${t.slug}`}
                                                    title="Email the pre-populated site-message to a contact address"
                                                    onClick={() => {
                                                        setSendMsgTenant(t);
                                                        setSendMsgTo(t.contact_email || "");
                                                    }}
                                                >
                                                    <Send className="h-4 w-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" asChild aria-label="Compliance pack" data-testid={`compliance-${t.slug}`}>
                                                    <a
                                                        href={`${API_URL}/admin/tenants/${t.tenant_id}/compliance-pack.zip`}
                                                        download
                                                        title="Download compliance pack (ZIP)"
                                                    >
                                                        <Package className="h-4 w-4" />
                                                    </a>
                                                </Button>
                                                <Button size="icon" variant="ghost" onClick={() => rotate(t.tenant_id)} aria-label="Rotate key" data-testid={`rotate-${t.slug}`}>
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => toggleSuppress(t)}
                                                    aria-label={t.suppress_dead_link_alerts ? "Resume dead-link alerts" : "Suppress dead-link alerts"}
                                                    data-testid={`suppress-${t.slug}`}
                                                    title={t.suppress_dead_link_alerts
                                                        ? "Dead-link alerts are SUPPRESSED — click to resume (scanner will flag broken destinations again)"
                                                        : "Suppress dead-link alerts (skip scanner + hide previously-flagged links for this tenant — useful during DNS-launch)"
                                                    }
                                                >
                                                    {t.suppress_dead_link_alerts
                                                        ? <BellOff className="h-4 w-4 text-amber-600" />
                                                        : <Bell className="h-4 w-4" />
                                                    }
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => copyFullEnv(t)}
                                                    aria-label="Copy full .env envelope"
                                                    data-testid={`copy-env-${t.slug}`}
                                                    title="Copy full .env — SpLink credentials pre-filled, SocialPulse SSO fields populated if uploaded (else <PASTE FROM SP> placeholders). One-click handoff to the site agent."
                                                >
                                                    <FileText className="h-4 w-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" onClick={() => del(t.tenant_id)} aria-label="Delete" disabled={t.slug === "splink"} data-testid={`del-${t.slug}`}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ));
                                })()}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Bulk-send site-messages to filtered tenants */}
            <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResults(null); } }}>
                <DialogContent data-testid="bulk-send-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk send site-messages</DialogTitle>
                    </DialogHeader>
                    {(() => {
                        const q = tenantSearch.trim().toLowerCase();
                        const filtered = (tenants || []).filter((t) => !q ||
                            (t.slug || "").toLowerCase().includes(q) ||
                            (t.name || "").toLowerCase().includes(q));
                        const withEmail = filtered.filter((t) => t.contact_email);
                        const withoutEmail = filtered.filter((t) => !t.contact_email);
                        if (bulkResults) {
                            return (
                                <div className="space-y-3">
                                    <p className="text-sm">
                                        <strong>{bulkResults.sent}</strong> sent,{" "}
                                        <strong>{bulkResults.skipped}</strong> skipped,{" "}
                                        <strong>{bulkResults.errors}</strong> errored (of {bulkResults.total}).
                                    </p>
                                    <div className="max-h-64 overflow-y-auto border border-border bg-muted/30 p-3 text-xs font-mono">
                                        {bulkResults.results.map((r) => (
                                            <div key={r.tenant_id} data-testid={`bulk-result-${r.slug || r.tenant_id}`}
                                                className={
                                                    r.status === "sent" ? "text-emerald-600 dark:text-emerald-400"
                                                    : r.status === "skipped" ? "text-amber-600 dark:text-amber-400"
                                                    : "text-red-600 dark:text-red-400"
                                                }>
                                                {r.status.toUpperCase().padEnd(8)} {(r.slug || r.tenant_id).padEnd(24)} {r.to || r.reason || ""}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-end">
                                        <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkResults(null); load(); }} data-testid="bulk-send-close">
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    This will email the pre-populated site-message (with each tenant's live production API key) to <strong>{withEmail.length}</strong> tenant{withEmail.length !== 1 ? "s" : ""} via Resend.
                                    {tenantSearch && <> Filter: <code className="text-primary">"{tenantSearch}"</code>.</>}
                                </p>
                                <div className="max-h-40 overflow-y-auto border border-border bg-muted/30 p-3 text-xs font-mono">
                                    {withEmail.map((t) => (
                                        <div key={t.tenant_id}>✓ {t.slug.padEnd(22)} → {t.contact_email}</div>
                                    ))}
                                    {withoutEmail.length > 0 && (
                                        <>
                                            <div className="mt-2 text-muted-foreground">Skipped (no contact_email — use ✉️ icon to set one):</div>
                                            {withoutEmail.map((t) => (
                                                <div key={t.tenant_id} className="text-amber-600 dark:text-amber-400">— {t.slug}</div>
                                            ))}
                                        </>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-send-cancel" disabled={bulkSending}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            setBulkSending(true);
                                            try {
                                                const { data } = await api.post("/admin/tenants/send-message-bulk", {
                                                    tenant_ids: withEmail.map((t) => t.tenant_id),
                                                });
                                                setBulkResults(data);
                                                toast.success(`${data.sent} sent · ${data.skipped} skipped · ${data.errors} errored`);
                                            } catch (e) {
                                                toast.error(`Bulk send failed: ${e?.response?.data?.detail || e.message}`);
                                            } finally { setBulkSending(false); }
                                        }}
                                        disabled={bulkSending || withEmail.length === 0}
                                        data-testid="bulk-send-confirm"
                                    >
                                        {bulkSending ? "Sending…" : `Send to ${withEmail.length}`}
                                    </Button>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Send site-message via Resend */}
            <Dialog open={!!sendMsgTenant} onOpenChange={(o) => !o && setSendMsgTenant(null)}>
                <DialogContent data-testid="send-message-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Email site-message to {sendMsgTenant?.name || ""}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Emails the pre-populated onboarding note (with the live production API key) to your sister-site contact via Resend. The address is saved on the tenant for future one-click sends.
                    </p>
                    <Label htmlFor="send-msg-to" className="mt-2">Recipient email</Label>
                    <Input
                        id="send-msg-to"
                        type="email"
                        value={sendMsgTo}
                        onChange={(e) => setSendMsgTo(e.target.value)}
                        placeholder={`engineering@${sendMsgTenant?.slug || "example"}.com`}
                        data-testid="send-message-to-input"
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setSendMsgTenant(null)} data-testid="send-message-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                if (!sendMsgTenant || !sendMsgTo.trim()) return;
                                setSending(true);
                                try {
                                    const { data } = await api.post(
                                        `/admin/tenants/${sendMsgTenant.tenant_id}/send-message`,
                                        { to: sendMsgTo.trim() },
                                    );
                                    toast.success(`Sent to ${data.to}`);
                                    setSendMsgTenant(null);
                                    setSendMsgTo("");
                                    load();
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Send failed");
                                } finally {
                                    setSending(false);
                                }
                            }}
                            disabled={sending || !sendMsgTo.trim()}
                            data-testid="send-message-submit"
                        >
                            {sending ? "Sending…" : "Send via Resend"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="border border-border bg-card p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-3 font-display text-3xl font-bold">{value}</div>
        </div>
    );
}
